import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { getActiveRole } from "../../../utils/auth";
import PageHeaderSection from "../../../components/layout/PageHeaderSection";
import { useReportSettings } from "../../../context/ReportSettingsContext";
import { tabsForRole } from "../../../config/reportDefinitions";
import CoachRevenueReport from "./CoachRevenueReport";
import SessionAttendanceReport from "./SessionAttendanceReport";
import SessionStatement from "../../student/SessionStatement";
import AcademyRevenueSummaryReport from "./AcademyRevenueSummaryReport";
import StudentAttendanceSummaryReport from "./StudentAttendanceSummaryReport";
import DataExport from "./DataExport";
import PaymentsDueReport from "./PaymentsDueReport";
import SessionsSummaryReport from "./SessionsSummaryReport";
import MonthlyStatementControl from "./MonthlyStatementControl";

export default function ReportsHub() {
  const { t } = useTranslation();
  const role = getActiveRole();
  const { settings, ready, loadError } = useReportSettings();
  const tabs = useMemo(
    () => (ready ? tabsForRole(role, settings, t) : []),
    [role, settings, ready, t]
  );
  const [activeTab, setActiveTab] = useState(() => {
    try {
      return new URLSearchParams(window.location.search).get("tab");
    } catch {
      return null;
    }
  });

  useEffect(() => {
    if (!ready) return;
    if (tabs.length === 0) {
      setActiveTab(null);
      return;
    }
    if (!activeTab || !tabs.some((tab) => tab.key === activeTab)) {
      setActiveTab(tabs[0].key);
    }
  }, [tabs, activeTab, ready]);

  const selectTab = (key) => {
    setActiveTab(key);
    try {
      const url = new URL(window.location.href);
      url.searchParams.set("tab", key);
      window.history.replaceState({}, "", url.pathname + url.search);
    } catch {
      /* ignore */
    }
  };

  if (!ready) {
    return (
      <div className="list-page flex items-center justify-center h-64 text-slate-400">
        {t("reports.loading")}
      </div>
    );
  }

  if (loadError && tabs.length === 0) {
    return (
      <div className="list-page">
        <PageHeaderSection
          eyebrow={t("reports.eyebrow")}
          title={t("reports.title")}
          subtitle={t("reports.subtitle")}
        />
        <div className="mx-6 rounded-xl border border-red-200 bg-red-50 px-6 py-10 text-center text-red-800" role="status">
          <p className="text-lg font-semibold">
            {t("reports.settingsLoadFailed", { defaultValue: "Could not load report settings" })}
          </p>
          <p className="text-sm mt-2 text-red-700">
            {t("reports.settingsLoadFailedHint", {
              defaultValue: "This is not the same as reports being disabled. Refresh to try again.",
            })}
          </p>
        </div>
      </div>
    );
  }

  if (tabs.length === 0) {
    return (
      <div className="list-page">
        <PageHeaderSection
          eyebrow={t("reports.eyebrow")}
          title={t("reports.title")}
          subtitle={t("reports.subtitle")}
        />
        <div className="mx-6 rounded-xl border border-amber-200 bg-amber-50 px-6 py-10 text-center text-amber-800">
          <p className="text-lg font-semibold">{t("reports.noReportsEnabled")}</p>
          <p className="text-sm mt-2 text-amber-700">
            {t("reports.noReportsHint")}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="list-page">
      <PageHeaderSection
        eyebrow={t("reports.eyebrow")}
        title={t("reports.title")}
        subtitle={t("reports.subtitle")}
      >
        {tabs.length > 1 && (
          <nav className="flex w-full min-w-0 flex-wrap gap-2" aria-label={t("reports.title")}>
            {tabs.map(tab => (
              <button
                key={tab.key}
                type="button"
                onClick={() => selectTab(tab.key)}
                className={`whitespace-nowrap rounded-lg border px-3 py-2 text-sm font-medium shadow-sm transition-colors ${
                  activeTab === tab.key
                    ? "border-primary-600 bg-primary-600 text-white"
                    : "border-slate-200 bg-white text-slate-700 hover:border-primary-300 hover:bg-primary-50 hover:text-primary-800"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        )}
      </PageHeaderSection>

      {loadError && (
        <div className="mx-6 mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900" role="status">
          {t("reports.settingsLoadFailedHint", {
            defaultValue: "Report settings could not be refreshed. Availability below may be outdated.",
          })}
        </div>
      )}

      <div>
        {activeTab === "coach-revenue" && <CoachRevenueReport embeddedInHub />}
        {activeTab === "session-statement" && <SessionStatement embeddedInHub />}
        {activeTab === "batch-attendance" && <SessionAttendanceReport />}
        {activeTab === "academy-revenue" && <AcademyRevenueSummaryReport />}
        {activeTab === "student-attendance" && <StudentAttendanceSummaryReport />}
        {activeTab === "payments-due" && <PaymentsDueReport />}
        {activeTab === "data-export" && <DataExport />}
        {activeTab === "sessions-summary" && <SessionsSummaryReport embeddedInHub />}
        {activeTab === "monthly-statements" && <MonthlyStatementControl embeddedInHub />}
      </div>
    </div>
  );
}
